import { useQuery } from '@tanstack/react-query';
import { Users, Calendar, MapPin, AlertCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';

export const TeamsWidget = () => {
  const { data: teamsData, isLoading } = useQuery({
    queryKey: ['teams-widget'],
    queryFn: async () => {
      const { data: teams } = await supabase
        .from('teams')
        .select(`
          id, 
          name,
          team_members(count),
          team_schedules(count)
        `);

      const { data: activeSchedules } = await supabase
        .from('team_schedules')
        .select('team_id')
        .gte('end_date', new Date().toISOString());

      const { data: emergencyReports } = await supabase
        .from('emergency_reports')
        .select('team_id')
        .eq('status', 'pending');

      const activeTeamIds = new Set(activeSchedules?.map(s => s.team_id) || []);
      const teamsWithEmergencies = new Set(emergencyReports?.map(r => r.team_id) || []);

      return {
        total: teams?.length || 0,
        active: activeTeamIds.size,
        withEmergencies: teamsWithEmergencies.size,
        totalMembers: teams?.reduce((sum, team) => sum + (team.team_members?.[0]?.count || 0), 0) || 0,
        totalSchedules: teams?.reduce((sum, team) => sum + (team.team_schedules?.[0]?.count || 0), 0) || 0,
        teams: teams || []
      };
    },
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-32 bg-muted rounded"></div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <Users className="w-6 h-6 text-green-500" />
        <h3 className="text-lg font-semibold">Teams Overview</h3>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{teamsData?.total || 0}</div>
          <div className="text-sm text-muted-foreground">Total Teams</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{teamsData?.totalMembers || 0}</div>
          <div className="text-sm text-muted-foreground">Team Members</div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-500" />
            <span className="text-sm">Active Schedules</span>
          </div>
          <Badge variant="default">{teamsData?.active || 0}</Badge>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm">Teams w/ Emergencies</span>
          </div>
          <Badge variant={teamsData?.withEmergencies ? "destructive" : "secondary"}>
            {teamsData?.withEmergencies || 0}
          </Badge>
        </div>
      </div>
    </Card>
  );
};